import { CMSExploreCategory } from '../services/exploreCategories';
import { Subcategory, subcategoriesService } from '../services/subcategories';

/** Build a category URL with optional nested subcategory path segments */
export function buildCategoryRoute(
  categoryKey?: string | null,
  subcategoryPath?: string | string[] | null,
): string | null {
  const category = categoryKey?.trim();
  if (!category) return null;

  const segments = Array.isArray(subcategoryPath)
    ? subcategoryPath.filter(Boolean)
    : subcategoryPath?.trim()
      ? [subcategoryPath.trim()]
      : [];

  if (segments.length === 0) {
    return `/category/${encodeURIComponent(category)}`;
  }

  const encodedPath = segments.map((s) => encodeURIComponent(s)).join('/');
  return `/category/${encodeURIComponent(category)}/${encodedPath}`;
}

/** Parse /category/:categoryId/* into category key and subcategory segments */
export function parseCategoryRoute(pathname: string): {
  categoryKey: string | null;
  subcategorySegments: string[];
} {
  const match = pathname.match(/^\/category\/([^/]+)(?:\/(.*))?$/);
  if (!match) {
    return { categoryKey: null, subcategorySegments: [] };
  }

  const categoryKey = decodeURIComponent(match[1]);
  const rest = match[2];
  const subcategorySegments = rest
    ? rest.split('/').map((s) => decodeURIComponent(s)).filter(Boolean)
    : [];

  return { categoryKey, subcategorySegments };
}

export function resolveExploreCategoryRoute(
  category: Pick<
    CMSExploreCategory,
    'link_url' | 'target_category_key' | 'target_subcategory_key' | 'category_key'
  >,
  subcategories?: Subcategory[],
): string | null {
  const customLink = category.link_url?.trim();
  if (customLink) return customLink;

  const categoryKey =
    category.target_category_key?.trim() || category.category_key?.trim();
  if (!categoryKey) return null;

  const subKey = category.target_subcategory_key?.trim();
  if (!subKey) return buildCategoryRoute(categoryKey);

  if (subcategories?.length) {
    const segments = subcategoriesService.getPathSegments(
      subcategories,
      subKey,
      categoryKey,
    );
    return buildCategoryRoute(categoryKey, segments);
  }

  return buildCategoryRoute(categoryKey, subKey);
}
